/*
 * Voyado Mini-Bag recommendations controller.
 * Fills the mini-bag YMAL with Voyado ADD_TO_CART_RECS (CART fallback), cloning the
 * section's [data-tmpl-minibag-rec-card] <template> per product. Config + cart keys
 * come from the [data-voyado-minibag-recs] shell attributes.
 * Re-fetches on mini-bag:updated, since refreshCart swaps in a fresh shell.
 */
;(function () {
  'use strict';

  // Per-shell single-fetch guard, keyed on the element → the cart-keys it was fetched for.
  var fetchedFor = new WeakMap();
  // Cross-shell cache of the last successful fetch. refreshCart swaps in a FRESH (empty) shell on every
  // cart re-render — including when only a gift-wrap line changed (its product_id is excluded from the
  // cart keys, so the product set is unchanged). In that case re-render the cached recs into the new shell
  // instead of hitting Voyado again, so the "You may also like" list doesn't reload/flicker on gift insert.
  var lastKeys = null;
  var lastProducts = null;

  function slot(root, name) { return root && root.querySelector('[data-slot="' + name + '"]'); }

  // ---- field extraction ----
  // safeUrl first, then the market root — prefixing an unvalidated value would turn a rejected
  // scheme into a path the allowlist accepts. withMarket also normalises the leading slash.
  function productUrl(p) {
    var safe = safeUrl((p && p.link) || '');
    if (!safe) return '#';
    return window.voyadoWithMarket ? window.voyadoWithMarket(safe) : safe;
  }
  function handleFromLink(p) {
    var link = (p && p.link) || '';
    return link.split('?')[0].replace(/\/+$/, '').split('/').pop() || '';
  }
  function safeUrl(url) {
    var s = (url == null ? '' : String(url)).replace(/[\t\n\r]/g, '').trim();
    if (!s) return '';
    if (/^(https?:)?\/\//i.test(s)) return s;
    if (/^[/?#]/.test(s)) return s;
    if (/^[\w.+-]+:/.test(s)) return '';        // reject any other explicit scheme
    return s;
  }
  function isShopifyCdn(url) { return /\/\/[^/]*cdn\.shopify\.com\//i.test(url || ''); }
  function cdnWidth(url, w) {
    if (!isShopifyCdn(url)) return url;
    var clean = url.replace(/([?&])width=\d+&?/i, '$1').replace(/[?&]$/, '');
    return clean + (clean.indexOf('?') >= 0 ? '&' : '?') + 'width=' + w;
  }
  function imageUrl(p, idx) {
    var info = (p && p.imageInfo) || {};
    var images = info.images || [];
    var img = images[idx];
    var src = (img && img.sources && img.sources[0] && img.sources[0].url)
      || (idx === 0 ? (info.thumbnail || '') : '');
    src = safeUrl(src);
    return src ? cdnWidth(src, 208) : '';
  }
  // Voyado sellingPrice is in major units (159 = 159.00), no /100. "from" on a price range. Same
  // formatter chain as the card renderer, so the market's currency holds even without the corrector.
  function formatPrice(price) {
    if (!price || price.min == null) return '';
    var bv = window.broraVoyado || {};
    var currency = (window.Shopify && window.Shopify.currency && window.Shopify.currency.active) || bv.currencyCode;
    var val = typeof bv.formatMoney === 'function' ? bv.formatMoney(price.min, currency) : null;
    if (val == null) {
      try {
        val = currency ? new Intl.NumberFormat('en-GB', { style: 'currency', currency: currency }).format(price.min) : String(price.min);
      } catch (e) { val = String(price.min); }
    }
    return price.min === price.max ? val : ('from ' + val);
  }

  function buildCard(template, product, isLast) {
    var first = template && template.content && template.content.firstElementChild;
    var node = first ? first.cloneNode(true) : null;
    if (!node) return null;
    var url = safeUrl(productUrl(product));
    var alt = product.title || product.name || '';
    var ticket = product.ticket || '';

    var imgLink = slot(node, 'image-link');
    if (imgLink) { imgLink.href = url; if (ticket) imgLink.setAttribute('data-voyado-ticket', ticket); }

    var img = slot(node, 'image');
    if (img) { img.src = imageUrl(product, 0); img.alt = alt; }

    var img2 = slot(node, 'image-secondary');
    if (img2) {
      var src2 = imageUrl(product, 1);
      if (src2) { img2.src = src2; img2.alt = alt; } else { img2.remove(); }
    }

    var title = slot(node, 'title');
    if (title) { title.href = url; title.textContent = alt; if (ticket) title.setAttribute('data-voyado-ticket', ticket); }

    var price = slot(node, 'price');
    if (price) price.textContent = formatPrice(product.sellingPrice);

    // Colour subtitle from the Accelerator feed's custom.colour_name (requested in presentCustom);
    // blank when the feed doesn't carry it, leaving the [data-slot="option"] span as a spacer.
    var option = slot(node, 'option');
    if (option) {
      var colourList = product.custom && product.custom.colour_name;
      var colourLabel = (colourList && colourList[0] && (colourList[0].label || colourList[0].id)) || '';
      if (colourLabel) option.textContent = colourLabel;
    }

    var atb = slot(node, 'atb');
    if (atb) {
      var handle = handleFromLink(product);
      if (handle) atb.setAttribute('data-product-handle', handle);
      else { atb.setAttribute('disabled', ''); atb.setAttribute('aria-disabled', 'true'); }
      if (ticket) atb.setAttribute('data-voyado-ticket', ticket);
    }

    // JS-cloned cards need the global BroraProductCardWishlist binder. product.key = product id.
    var wish = slot(node, 'wishlist');
    if (wish) {
      if (product.key) {
        wish.setAttribute('data-wishlist-product-id', product.key);
        wish.setAttribute('aria-label', 'Add ' + (alt || 'product') + ' to wishlist');
        wish.setAttribute('data-wishlist-label-add', 'Add ' + (alt || 'product') + ' to wishlist');
        wish.setAttribute('data-wishlist-label-remove', 'Remove ' + (alt || 'product') + ' from wishlist');
        if (window.BroraProductCardWishlist && window.BroraProductCardWishlist.bind) {
          wish.setAttribute('data-wl-bound', '');   // guard against mini-bag.js double-bind
          window.BroraProductCardWishlist.bind(wish);
        }
      } else {
        wish.remove();
      }
    }

    if (isLast) node.classList.remove('border-b', 'border-rule-tertiary');
    return node;
  }

  function render(shell, products) {
    var template = shell.querySelector('[data-tmpl-minibag-rec-card]');
    var list = shell.querySelector('[data-mini-bag-ymal-list]');
    if (!template || !list) return false;
    var frag = document.createDocumentFragment();
    var added = 0;
    products.forEach(function (p, i) {
      var card = buildCard(template, p, i === products.length - 1);
      if (card) { frag.appendChild(card); added++; }
    });
    if (!added) return false;
    list.replaceChildren(frag);
    // Correct the Voyado feed price to the market's Storefront price (mirrors the PLP/carousel cards),
    // so converted markets show the market's own value, not the feed's.
    if (window.broraVoyado && window.broraVoyado.applyPrices) window.broraVoyado.applyPrices(list);
    return true;
  }

  function hide(shell) { shell.classList.add('hidden'); }
  function show(shell) { shell.classList.remove('hidden'); }

  function load(shell) {
    var keys = (shell.getAttribute('data-voyado-minibag-cart') || '').trim();
    if (!keys) { hide(shell); return; }            // empty cart — nothing to recommend
    if (fetchedFor.get(shell) === keys) return;
    fetchedFor.set(shell, keys);
    // Product set unchanged since the last fetch (e.g. only a gift-wrap line changed) → re-render the cached
    // recs into this fresh shell, no Voyado round-trip and no flicker.
    if (keys === lastKeys && lastProducts && lastProducts.length) {
      if (render(shell, lastProducts)) show(shell); else hide(shell);
      return;
    }
    if (!window.VoyadoClient || !window.VoyadoClient.cartPage) { hide(shell); return; }

    var rawLimit = parseInt(shell.getAttribute('data-recommendation-limit'), 10);
    var limit = rawLimit > 0 ? Math.min(24, rawLimit) : 12;

    // ADD_TO_CART_RECS base = newest cart item (cart.items is newest-first, so key[0]).
    var baseKey = keys.split('|')[0];

    function pickProducts(res) {
      var list = res && res.recommendationLists && res.recommendationLists[0];
      return ((list && list.productGroups) || [])
        .map(function (g) { return (g && g.products && g.products[0]) || null; })
        .filter(Boolean)
        .slice(0, limit);
    }

    var params = { cart: keys, presentCustom: 'swatch_image_url|product_badges|colour_name' };

    window.VoyadoClient.cartPage(
      params,
      { recommendationLists: [{ id: 'minibag', algorithm: 'ADD_TO_CART_RECS', limit: limit, params: { productKey: baseKey } }] }
    ).then(function (res) {
      var products = pickProducts(res);
      if (products.length) return products;
      // Fallback: whole-cart recs, so the drawer never sits empty.
      return window.VoyadoClient.cartPage(
        params,
        { recommendationLists: [{ id: 'minibag', algorithm: 'CART', limit: limit }] }
      ).then(pickProducts);
    }).then(function (products) {
      if (!products.length || !render(shell, products)) { hide(shell); return; }
      lastKeys = keys; lastProducts = products;   // cache for cross-shell (post-refreshCart) re-use
      show(shell);
    }).catch(function () {
      fetchedFor.delete(shell); // allow a retry on the next open
      hide(shell);
    });
  }

  function initAll(root) {
    var r = (root && root.querySelectorAll) ? root : document;
    r.querySelectorAll('[data-voyado-minibag-recs]').forEach(load);
  }

  // Hide recs as soon as the LAST line item is sent to removal (is-remove-pending /
  // is-wishlist-pending), instead of waiting out the 3s undo window; undo restores them.
  function refreshPendingVisibility() {
    var container = document.querySelector('[data-mini-bag]');
    if (!container) return;
    var remaining = 0;
    container.querySelectorAll('[data-line-item-key]').forEach(function (li) {
      if (!li.classList.contains('is-remove-pending') && !li.classList.contains('is-wishlist-pending')) remaining++;
    });
    container.querySelectorAll('[data-mini-bag-ymal]').forEach(function (ymal) {
      var list = ymal.querySelector('[data-mini-bag-ymal-list]');
      var hasCards = !!(list && list.children.length);
      if (remaining === 0) ymal.classList.add('hidden');
      else if (hasCards) ymal.classList.remove('hidden');
    });
  }

  // Fetch lazily on open (cart-icon click or mini-bag:open), re-fetch on mini-bag:updated.
  document.addEventListener('click', function (e) {
    if (e.target.closest('[data-mini-bag-trigger]')) initAll();
    // Remove / move-to-wishlist / undo → recompute next tick, once the pending class is set.
    if (e.target.closest('[data-mini-bag-remove], [data-mini-bag-undo], [data-mini-bag-wishlist-toggle]')) {
      setTimeout(refreshPendingVisibility, 0);
    }
  });
  document.addEventListener('mini-bag:open', function () { initAll(); });
  document.addEventListener('mini-bag:updated', function () { initAll(); });

  // Arriving from another open drawer reaches neither trigger above: assets/drawer-coordinator.js owns
  // that tap and opens the bag through its own API. Its active-drawer signal covers every entry point.
  var root = document.documentElement;
  new MutationObserver(function () {
    if (root.getAttribute('data-drawers-active') === 'bag') initAll();
  }).observe(root, { attributes: true, attributeFilter: ['data-drawers-active'] });
})();
